import { updateConceptProgress, updateSubtopicProgress, updateTopicProgress, updateSubjectProgress } from '../services/subjectPracticeService';

// Local storage key
const QUEUE_KEY = 'qb_progress_queue_v1';

// Settings
const MAX_RETRY = 5;
const BASE_DELAY_MS = 1000; // 1s

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch (e) {
    console.error('Failed to read progress queue', e);
    return [];
  }
}

function writeQueue(queue) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (e) {
    console.error('Failed to write progress queue', e);
  }
}

function enqueue(update) {
  const queue = readQueue();
  const item = {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    createdAt: new Date().toISOString(),
    attempts: 0,
    nextAttemptAt: Date.now(),
    ...update
  };
  queue.push(item);
  writeQueue(queue);
  return item;
}

function dequeue(id) {
  let queue = readQueue();
  queue = queue.filter(i => i.id !== id);
  writeQueue(queue);
}

function backoffDelay(attempt) {
  return Math.min(BASE_DELAY_MS * Math.pow(2, attempt), 60 * 1000);
}

async function processItem(item) {
  const { type, userId, targetId, payload } = item;

  try {
    let res = null;
    switch (type) {
      case 'concept':
        res = await updateConceptProgress(userId, targetId, payload);
        break;
      case 'subtopic':
        res = await updateSubtopicProgress(userId, targetId, payload);
        break;
      case 'topic':
        res = await updateTopicProgress(userId, targetId, payload);
        break;
      case 'subject':
        res = await updateSubjectProgress(userId, targetId, payload);
        break;
      default:
        throw new Error('Unknown progress type ' + type);
    }

    // Consider success if response doesn't indicate failure
    if (res && res.success === false) {
      throw new Error(res.error || 'API returned success:false');
    }

    // On success remove from queue
    dequeue(item.id);
    return { success: true, item };
  } catch (err) {
    // increment attempts and schedule nextAttempt
    const queue = readQueue();
    const found = queue.find(q => q.id === item.id);
    if (found) {
      found.attempts = (found.attempts || 0) + 1;
      found.nextAttemptAt = Date.now() + backoffDelay(found.attempts);
      writeQueue(queue);
    }

    // If attempts exceeded, drop and return failure
    if ((item.attempts || 0) + 1 >= MAX_RETRY) {
      dequeue(item.id);
      return { success: false, item, error: err?.message || err };
    }

    return { success: false, item, error: err?.message || err };
  }
}

let _processorInterval = null;
let _isProcessing = false;

function processQueueOnce() {
  // Do not process when tab is hidden — avoid zombie requests
  if (typeof document !== 'undefined' && document.hidden) return;
  if (_isProcessing) return;
  _isProcessing = true;
  const queue = readQueue();
  const now = Date.now();
  const due = queue.filter(i => (i.nextAttemptAt || 0) <= now);
  // if there are no due items or all items lack a userId, skip processing to avoid unauthenticated calls
  if (due.length === 0) {
    _isProcessing = false;
    return;
  }
  // process sequentially
  const p = due.reduce((prev, item) => prev.then(() => processItem(item)), Promise.resolve());
  p.finally(() => { _isProcessing = false; });
}

export function startQueueProcessor(intervalMs = 5000) {
  if (_processorInterval) return;
  // run immediately then on interval
  processQueueOnce();
  _processorInterval = setInterval(processQueueOnce, intervalMs);
}

export function stopQueueProcessor() {
  if (_processorInterval) {
    clearInterval(_processorInterval);
    _processorInterval = null;
  }
}

export function listQueue() {
  return readQueue();
}

export default {
  enqueue,
  listQueue,
  startQueueProcessor,
  stopQueueProcessor
};
